import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { Spinner } from '@ui'
import { getJSON } from './api.js'

// Sign-in state. The server keeps the session in an HttpOnly cookie (see internal/auth), so the
// browser never stores a token: it asks /api/session who's signed in. Firebase is only used for
// the Google sign-in popup itself, loaded on demand.
const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [state, setState] = useState({ status: 'loading', email: null, admin: false, config: null })
  const [error, setError] = useState(null)

  const refresh = useCallback(async () => {
    try {
      const config = await getJSON('/api/auth-config')
      try {
        const session = await getJSON('/api/session')
        setState({ status: 'signed-in', email: session.email, admin: !!session.admin, config })
      } catch {
        setState({ status: 'signed-out', email: null, admin: false, config })
      }
    } catch (err) {
      setError(`Couldn't reach the server: ${err.message}`)
      setState((s) => ({ ...s, status: 'signed-out' }))
    }
  }, [])

  useEffect(() => {
    refresh()
    // api.js announces a 401 (e.g. the session expired) so every page drops to signed-out.
    const onExpired = () => setState((s) => ({ ...s, status: 'signed-out', email: null, admin: false }))
    window.addEventListener('wl-signed-out', onExpired)
    return () => window.removeEventListener('wl-signed-out', onExpired)
  }, [refresh])

  const signIn = useCallback(async () => {
    setError(null)
    try {
      const [{ initializeApp, getApps }, { getAuth, GoogleAuthProvider, signInWithPopup, signOut }] =
        await Promise.all([import('firebase/app'), import('firebase/auth')])
      const app = getApps()[0] ?? initializeApp(state.config.firebase)
      const firebaseAuth = getAuth(app)
      const result = await signInWithPopup(firebaseAuth, new GoogleAuthProvider())
      const idToken = await result.user.getIdToken()
      // The site's own session replaces Firebase's, so don't keep Firebase signed in.
      await signOut(firebaseAuth)
      const res = await fetch('/api/session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id_token: idToken }),
      })
      const body = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(body.error ?? `HTTP ${res.status}`)
      setState((s) => ({ ...s, status: 'signed-in', email: body.email, admin: !!body.admin }))
    } catch (err) {
      if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') return
      setError(err.message)
    }
  }, [state.config])

  const signOutOfSite = useCallback(async () => {
    await fetch('/api/session', { method: 'DELETE' }).catch(() => {})
    setState((s) => ({ ...s, status: 'signed-out', email: null, admin: false }))
  }, [])

  return (
    <AuthContext.Provider value={{ ...state, error, signIn, signOut: signOutOfSite }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}

// Wraps pages that need a signed-in user; sends everyone else to the landing page.
export function RequireAuth({ children }) {
  const { status } = useAuth()
  if (status === 'loading') return <Spinner />
  if (status !== 'signed-in') return <Navigate to="/" replace />
  return children
}
